import { FindingEndpointAssetApiProtocolSchema } from '@terra/shared-contracts-findings';
import {
  isAiAssetTags,
  isGrpcAssetTags,
  isWebSocketAssetTags,
} from '@terra/shared-schemas-orchestrator';
import { deriveXmlOperationLabel } from '@terra/shared-utils';
import type { FindingAssetApiProtocol } from '../types/enrichedFindings';

export type EndpointLikeWithOperations = {
  soapOperationName?: string | null;
  graphqlOperationName?: string | null;
  xmlOperationName?: string | null;
  xmlOperationDisplayName?: string | null;
  mcpOperationName?: string | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
  grpc?: {
    service?: string | null;
    method?: string | null;
  } | null;
  assetTags?: readonly string[] | null;
  url?: string | null;
  path?: string | null;
  request?: { method?: string | null; bodyType?: string | null } | null;
};

export type WebSocketTransportLabel = 'WSS' | 'WS';

export function getWebSocketTransportLabel(
  url: string | null | undefined,
): WebSocketTransportLabel | null {
  if (!url) {
    return null;
  }

  try {
    const protocol = new URL(url).protocol;

    if (protocol === 'wss:') return 'WSS';
    if (protocol === 'ws:') return 'WS';

    return null;
  } catch {
    if (url.startsWith('wss://')) return 'WSS';
    if (url.startsWith('ws://')) return 'WS';

    return null;
  }
}

export function isWebSocketEndpoint(endpoint: {
  assetTags?: readonly string[] | null;
  url?: string | null;
}): boolean {
  return (
    isWebSocketAssetTags(endpoint.assetTags) ||
    getWebSocketTransportLabel(endpoint.url) !== null
  );
}

export function isGrpcEndpoint(endpoint: {
  assetTags?: readonly string[] | null;
  grpcServiceName?: string | null;
  grpcMethodName?: string | null;
  grpc?: {
    service?: string | null;
    method?: string | null;
  } | null;
  request?: { bodyType?: string | null } | null;
}): boolean {
  return (
    isGrpcAssetTags(endpoint.assetTags) ||
    Boolean(endpoint.grpcServiceName?.trim()) ||
    Boolean(endpoint.grpcMethodName?.trim()) ||
    Boolean(endpoint.grpc?.service?.trim()) ||
    Boolean(endpoint.grpc?.method?.trim()) ||
    endpoint.request?.bodyType === 'PROTOBUF'
  );
}

function formatGrpcRpcName(
  endpoint: EndpointLikeWithOperations,
): string | null {
  const service =
    endpoint.grpcServiceName?.trim() || endpoint.grpc?.service?.trim();
  const method =
    endpoint.grpcMethodName?.trim() || endpoint.grpc?.method?.trim();

  if (!service && !method) {
    return null;
  }

  return [service, method].filter(Boolean).join('/');
}

export function getEndpointMethodLabel(
  endpoint: EndpointLikeWithOperations,
): string {
  const transport = getWebSocketTransportLabel(endpoint.url);

  if (isWebSocketEndpoint(endpoint)) {
    return transport ?? 'WSS';
  }

  return endpoint.request?.method ?? '';
}

export function getEndpointAssetTitle(
  endpoint: EndpointLikeWithOperations,
): string {
  if (endpoint.path) {
    return endpoint.path;
  }

  if (isWebSocketEndpoint(endpoint) && endpoint.url) {
    return getWebSocketPathFromUrl(endpoint.url);
  }

  return endpoint.url ?? '';
}

function getWebSocketPathFromUrl(url: string): string {
  try {
    const { pathname, search } = new URL(url);

    return `${pathname}${search}` || '/';
  } catch {
    return '/';
  }
}

export function getEndpointApiTypeLabel(
  endpoint: EndpointLikeWithOperations,
): FindingAssetApiProtocol {
  if (isWebSocketEndpoint(endpoint)) {
    return 'WebSocket';
  }

  if (isGrpcEndpoint(endpoint)) {
    return FindingEndpointAssetApiProtocolSchema.enum.gRPC;
  }

  if (endpoint.soapOperationName) {
    return FindingEndpointAssetApiProtocolSchema.enum.SOAP;
  }

  if (endpoint.graphqlOperationName) {
    return FindingEndpointAssetApiProtocolSchema.enum.GraphQL;
  }

  if (endpoint.xmlOperationName) {
    return FindingEndpointAssetApiProtocolSchema.enum.XML;
  }

  if (endpoint.mcpOperationName) {
    return FindingEndpointAssetApiProtocolSchema.enum.MCP;
  }

  return FindingEndpointAssetApiProtocolSchema.enum.REST;
}

export function getEndpointDisplayLabel(
  endpoint: EndpointLikeWithOperations,
): string {
  if (isAiAssetTags(endpoint.assetTags)) return 'AI';

  return getEndpointApiTypeLabel(endpoint);
}

export function isAiEndpoint(endpoint: {
  assetTags?: readonly string[] | null;
}): boolean {
  return isAiAssetTags(endpoint.assetTags);
}

export function getEndpointOperationName(
  endpoint: EndpointLikeWithOperations,
): string | null {
  return (
    resolveNonMcpEndpointOperationName(endpoint) ??
    endpoint.mcpOperationName ??
    null
  );
}

export function getEndpointSignalGroupOperationName(
  endpoint: EndpointLikeWithOperations,
): string | null {
  return resolveNonMcpEndpointOperationName(endpoint);
}

function resolveNonMcpEndpointOperationName(
  endpoint: EndpointLikeWithOperations,
): string | null {
  return (
    endpoint.soapOperationName ??
    endpoint.graphqlOperationName ??
    endpoint.xmlOperationDisplayName ??
    deriveXmlOperationLabel(endpoint.xmlOperationName) ??
    formatGrpcRpcName(endpoint) ??
    null
  );
}
